import { useState } from "react";
import { Sparkles, ShieldCheck, Tag, Heart, Check, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { toast } from "sonner";

const PERKS = [
  { icon: Sparkles, title: "Double points on your 1st order", text: "Join now and earn 2× loyalty points on your very first purchase." },
  { icon: Tag, title: "First dibs on drops", text: "Get the best new & gently-used beauty before it sells out." },
  { icon: ShieldCheck, title: "Escrow-protected", text: "Every purchase is held safely until you confirm delivery." },
  { icon: Heart, title: "Sell & keep 85%", text: "Turn your unused makeup into cash in minutes." },
];

function getSource() {
  const p = new URLSearchParams(window.location.search);
  return p.get("utm_source") || p.get("from") || "landing";
}

export default function Join() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  usePageMeta(
    "Join Beauty Trade Club — Buy, Sell & Trade Beauty",
    "Join thousands of beauty lovers buying, selling and trading makeup safely. Sign up free for first dibs on drops and the best deals.",
    "/join"
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setLoading(true);
    try {
      await api.post("/leads", { email, name, source: getSource() });
      setDone(true);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16 bg-gradient-to-b from-[#fff6f0] to-white" data-testid="join-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[#E8956B] text-xs font-extrabold uppercase tracking-[0.3em] mb-3">
            <Sparkles size={14} /> Join the Club
          </div>
          <h1 className="font-display text-4xl font-black text-neutral-900 leading-tight">
            Beauty you'll love, <span className="text-[#E8956B]">for less</span>.
          </h1>
          <p className="mt-3 text-neutral-500">
            Buy, sell &amp; trade new and gently-used makeup — safely, with a community you can trust.
          </p>
        </div>

        {done ? (
          <div data-testid="join-success" className="bg-white border border-neutral-200 rounded-3xl p-8 text-center shadow-sm">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
              <Check size={28} />
            </div>
            <h2 className="font-display text-2xl font-bold text-neutral-900">You're in! 💗</h2>
            <p className="text-neutral-500 mt-2">We'll send the best beauty drops and deals your way. Start exploring now:</p>
            <a href="/shop" data-testid="join-shop-link" className="inline-block mt-5 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-8 py-3 transition-all">
              Browse the marketplace
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm space-y-4" data-testid="join-form">
            <input
              data-testid="join-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First name (optional)"
              className="w-full bg-neutral-50 border border-neutral-300 rounded-xl px-4 py-3 outline-none focus:border-[#E8956B]"
            />
            <input
              data-testid="join-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              required
              className="w-full bg-neutral-50 border border-neutral-300 rounded-xl px-4 py-3 outline-none focus:border-[#E8956B]"
            />
            <button
              data-testid="join-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-3.5 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />} Get free access
            </button>
            <p className="text-center text-xs text-neutral-400">Free to join · No spam · Unsubscribe anytime</p>
          </form>
        )}

        <div className="mt-8 space-y-3">
          {PERKS.map((p) => (
            <div key={p.title} className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#fff1e8] text-[#E8956B] flex items-center justify-center shrink-0">
                <p.icon size={18} />
              </div>
              <div>
                <div className="font-bold text-neutral-900 text-sm">{p.title}</div>
                <div className="text-sm text-neutral-500">{p.text}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
